import { useState, useEffect } from 'react';
import { useCommonOptions } from './useCommonOptions';
import { CustomMapping } from '../types/mapping';
import { useApi } from '../services/useApi';
import { useQuery, useQueryClient } from '@tanstack/react-query';
export interface DynamicMapping {
  vatZones: Record<string, string>;
  paymentTerms: Record<string, string>;
  customerGroups: Record<string, string>;
  currencies: Record<string, string>;
  units: Record<string, string>;
  accountTypeOptions: Record<string, string>;
  vatCodes: Record<string, string>;
  entryTypeOptions: Record<string, string>;
  keyFigureCodes: Record<string, string>;
}

interface UseDynamicMappingsReturn {
  mappings: DynamicMapping;
  customMappings: CustomMapping[];
  isLoading: boolean;
  error: string | null;
  getMappedValue: (field: string, value: string, item?: any,rootMapping?: any,sourceDataHeaderRow?: any) => string;
  addCustomMapping: (mapping: CustomMapping) => Promise<void>;
  removeCustomMapping: (id: string) => Promise<void>;
}

export const useDynamicMappings = (): UseDynamicMappingsReturn => {
  const { currencies, paymentTerms, vatZones, customerGroups, units, isLoading, accountTypeOptions, vatCodes, entryTypeOptions, keyFigureCodes } = useCommonOptions();
  const [mappings, setMappings] = useState<DynamicMapping>({
    vatZones: {},
    paymentTerms: {},
    customerGroups: {},
    currencies: {},
    units: {},
    accountTypeOptions: {},
    vatCodes: {},
    entryTypeOptions: {},
    keyFigureCodes: {}
  });
  const [customMappings, setCustomMappings] = useState<CustomMapping[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const api = useApi();
  const queryClient = useQueryClient();

  const { data, isLoading: isQueryLoading } = useQuery(
    ['customMappings'],
    async () => {
      const response = await api.getFieldMapping();
      return response?.data?.data ?? [];
    },
    {
      onSuccess: (data) => {
        setCustomMappings(data);
        setError(null);
      },
      onError: (err) => {
        console.error('Error loading custom mappings:', err);
        setError('Failed to load custom mappings');
      },
    }
  );

  useEffect(() => {
    if (!isLoading) {
      setMappings({
        vatZones: vatZones.reduce((acc, item) => {
          acc[item.value] = item.label;
          return acc;
        }, {} as Record<string, string>),
        paymentTerms: paymentTerms.reduce((acc, item) => {
          acc[item.value] = item.label;
          return acc;
        }, {} as Record<string, string>),
        customerGroups: customerGroups.reduce((acc, item) => {
          acc[item.value] = item.label;
          return acc;
        }, {} as Record<string, string>),
        currencies: currencies.reduce((acc, item) => {
          acc[item.value] = item.label;
          return acc;
        }, {} as Record<string, string>),
        units: units.reduce((acc, item) => {
          acc[item.value] = item.label;
          return acc;
        }, {} as Record<string, string>),
        accountTypeOptions: accountTypeOptions.reduce((acc, item) => {
          acc[item.value] = item.label;
          return acc;
        }, {} as Record<string, string>),
        vatCodes: vatCodes.reduce((acc, item) => {
          acc[item.value] = item.label;
          return acc;
        }, {} as Record<string, string>),
        entryTypeOptions: entryTypeOptions.reduce((acc, item) => {
          acc[item.value] = item.label;
          return acc;
        }, {} as Record<string, string>),
        keyFigureCodes: keyFigureCodes.reduce((acc, item) => {
          acc[item.value] = item.label;
          return acc;
        }, {} as Record<string, string>)
      });
    }
  }, [currencies, paymentTerms, vatZones, customerGroups, units, isLoading, accountTypeOptions, vatCodes, entryTypeOptions, keyFigureCodes]);

  const getValueFromJson = (obj: Record<string, any>, key: string): any => {
    const normalizedKey = Object.keys(obj).find(
      k => k.toLowerCase() === key.toLowerCase()
    );
    return normalizedKey ? obj[normalizedKey] : undefined;
  };

  const getIndexFromHeaderRow = (arr: string[], value: string): number | undefined => {
    if (!arr || !Array.isArray(arr) || !value) return undefined;
    const index = arr.findIndex(k => k && k.toLowerCase() === value.toLowerCase());
    return index !== -1 ? index : undefined;
  };

  const matchWildcard = (pattern: string, text: string): boolean => {
    if (!pattern || !text) return false;
    
    // Handle special cases
    if (pattern === "*") return true;
    if (pattern === text) return true;
    
    // Convert wildcard pattern to regex
    // * matches any sequence of characters
    // ? matches any single character
    const regexPattern = pattern
      .replace(/[.+^${}()|[\]\\]/g, '\\$&') // Escape special regex chars except * and ?
      .replace(/\*/g, '.*') // Convert * to .*
      .replace(/\?/g, '.'); // Convert ? to .
    
    try {
      const regex = new RegExp(`^${regexPattern}$`, 'i'); // Case insensitive
      return regex.test(text);
    } catch (error) {
      // If regex is invalid, fall back to exact match
      return pattern.toLowerCase() === text.toLowerCase();
    }
  };

  const compareNumeric = (operator: string, itemValue: string, compareValue: string): boolean => {
    const numItem = parseFloat(itemValue);
    const numCompare = parseFloat(compareValue);
    
    // Check if both values are valid numbers
    if (isNaN(numItem) || isNaN(numCompare)) {
      return false;
    }
    
    switch (operator) {
      case '>':
        return numItem > numCompare;
      case '<':
        return numItem < numCompare;
      case '>=':
        return numItem >= numCompare;
      case '<=':
        return numItem <= numCompare;
      default:
        return false;
    }
  };

  const getMappedValue = (field: string, value: string, item?: any,rootMapping?: any,sourceDataHeaderRow?: any): string => {
    if (value === null) return value;
    for (const customMapping of customMappings) {
      if (customMapping.field === field) {

        if (customMapping.mappings.length > 0) {
          for (const mapping of customMapping.mappings) {
            if (String(mapping.field) === String(value)) {
              if (mapping.extraFields) {
                
                let matchAll = true;
                for (const extraField of mapping.extraFields) {
        
                  const index = mapping.extraFields.indexOf(extraField);
                  const extraValue = String(mapping.extraValues[index]);
                  let colIndex = getIndexFromHeaderRow(sourceDataHeaderRow,extraField);
            
                  if(colIndex){
                    colIndex = parseInt(colIndex.toString());
                  }
                 
                  const itemValue = String(item[colIndex] ?? '').trim();
              
                  if(extraValue === "empty"){
                    if(itemValue.length > 0){
                      matchAll = false;
                    }
                  }else if(extraValue === "not_empty"){
                    if(itemValue.length === 0){
                      matchAll = false;
                    }
                  }else if(extraValue === "*"){
                    // Any value matches - always true, continue to next condition
                    continue;
                  }else if(extraValue.includes('*') || extraValue.includes('?')){
                    // Wildcard pattern matching
                    if (!matchWildcard(extraValue, itemValue)) {
                      matchAll = false;
                    }
                  }else if(extraValue.startsWith('>=') || extraValue.startsWith('<=') || extraValue.startsWith('>') || extraValue.startsWith('<')){
                    // Numeric comparison
                    let operator = '';
                    let compareValue = '';
                    
                    if (extraValue.startsWith('>=')) {
                      operator = '>=';
                      compareValue = extraValue.substring(2);
                    } else if (extraValue.startsWith('<=')) {
                      operator = '<=';
                      compareValue = extraValue.substring(2);
                    } else if (extraValue.startsWith('>')) {
                      operator = '>';
                      compareValue = extraValue.substring(1);
                    } else if (extraValue.startsWith('<')) {
                      operator = '<';
                      compareValue = extraValue.substring(1);
                    }
                    
                    if (!compareNumeric(operator, itemValue, compareValue)) {
                      matchAll = false;
                    }
                  }else {
                    // Equal (exact match) - case insensitive
                    if (itemValue.toLowerCase() !== extraValue.toLowerCase()) {
                      matchAll = false;
                    }
                  }
                }
                if(matchAll){
                  return mapping.value;
                }
              } else {
                return mapping.value;
              }
            }
          }
        }
      }
    }
    return value;
  };

  const addCustomMapping = async (mapping: CustomMapping) => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.createFieldMapping(mapping);
      setCustomMappings(prevMappings => {
        const existingIndex = prevMappings.findIndex(m => m.field === mapping.field);
        if (existingIndex >= 0) {
          const updatedMappings = [...prevMappings];
          updatedMappings[existingIndex] = { ...updatedMappings[existingIndex], ...response.data.data };
          return updatedMappings;
        }
        return [...prevMappings, response.data.data];
      });
      queryClient.invalidateQueries(['customMappings']);
    } catch (err) {
      setError('Failed to add custom mapping');
      console.error('Error adding custom mapping:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const removeCustomMapping = async (id: string) => {
    try {
      setLoading(true);
      setError(null);
      await api.deleteFieldMapping(id);
      setCustomMappings(prevMappings => prevMappings.filter(m => m.id !== id));
      queryClient.invalidateQueries(['customMappings']);
    } catch (err) {
      setError('Failed to remove custom mapping');
      console.error('Error removing custom mapping:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    mappings,
    customMappings,
    isLoading: isQueryLoading,
    error,
    getMappedValue,
    addCustomMapping,
    removeCustomMapping
  };
}; 